/**
 * Reference: https://github.com/Vicimus/games-demo/blob/main/src/utils/utils.ts
 */

import { WHEEL_PALETTE } from './constants';
import { Prize } from '../interfaces/prize';
import { Segment } from '../interfaces/segment';

export function darkenHex(hex: string, amount: number): string {
    const clamp = (val: number): number => Math.max(0, Math.min(255, val));
    const num = parseInt(hex.replace(/^#/, ''), 16);
    const r = clamp((num >> 16) - amount);
    const g = clamp(((num >> 8) & 0x00FF) - amount);
    const b = clamp((num & 0x0000FF) - amount);

    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
}

export function segmentArcPath(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}


export function segmentPath(cx: number, cy: number, r: number, start: number, end: number): string {
    const startRad = (Math.PI / 180) * start;
    const endRad = (Math.PI / 180) * end;

    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);

    const largeArcFlag = end - start <= 180 ? 0 : 1;

    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
}

export function segmentTitleWrap(text: string, maxLen: number = 13, maxLines: number = 3): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let current = '';
    let index = 0;

    for (; index < words.length; index++) {
        const word = words[index];
        const candidate = current ? `${current} ${word}` : word;

        if (candidate.length <= maxLen) {
            current = candidate;
            continue;
        }

        if (current) {
            lines.push(current);
            current = '';
        }

        if (lines.length >= maxLines) break;
        current = word.length > maxLen ? word.slice(0, maxLen) : word;
    }

    if (current && lines.length < maxLines) {
        lines.push(current);
        current = '';
        index = words.length;
    }

    // Only add an ellipsis when text genuinely did not fit; otherwise let the
    // font-size shrink to fit the full label (see computeWheelSegments).
    const overflowed = index < words.length || current !== '';
    if (overflowed && lines.length) {
        lines[lines.length - 1] = `${lines[lines.length - 1].slice(0, maxLen - 1)}…`;
    } else if (lines.length === 0) {
        lines.push(text.slice(0, maxLen));
    }

    return lines;
}

export function segmentTextTransform(segment: { startAngle: number; endAngle: number }): string {
    const angle = (segment.startAngle + segment.endAngle) / 2;
    const radius = 60;
    const rad = (Math.PI / 180) * angle;
    const x = 100 + radius * Math.cos(rad);
    const y = 100 + radius * Math.sin(rad);

    return `translate(${x}, ${y}) rotate(${angle})`;
}

function polarToCartesian(cx: number, cy: number, radius: number, angleInDegrees: number): { x: number, y: number } {
    const angleInRadians = angleInDegrees * Math.PI / 180.0;

    return {
        x: cx + (radius * Math.cos(angleInRadians)),
        y: cy + (radius * Math.sin(angleInRadians)),
    };
}

/**
 * Generate N visually distinct colors for a wheel (adjacent colors contrast).
 *
 * - Evenly distributes hues across the color wheel.
 * - Randomizes lightness slightly so neighbors look different.
 */
export function generateWheelColors(n: number): { background: string, text: string }[] {
    const colors = Array.from({ length: n }, (_, i) => WHEEL_PALETTE[i % WHEEL_PALETTE.length]);

    // Prevent same-color adjacent
    if (n > 2 && colors[n - 1] === colors[0]) {
        const forbidden = new Set([colors[0], colors[n - 2]]);
        const replacement = WHEEL_PALETTE.find(c => !forbidden.has(c));
        if (replacement) colors[n - 1] = replacement;
    }

    return colors;
}

/**
 * Given a progress fraction (0–1 of total rotation), returns the time fraction (0–1)
 * based on the wheel's CSS transition: cubic-bezier(0.25, 0.1, 0.25, 1).
 *
 * Uses Newton-Raphson to invert bezierY(t) = progress, then evaluates bezierX(t).
 */
export function bezierProgressToTime(progress: number): number {
    const p1x = 0.25, p1y = 0.1, p2x = 0.25, p2y = 1.0;

    const ay = 3 * p1y - 3 * p2y + 1;
    const by = 3 * p2y - 6 * p1y;
    const cy = 3 * p1y;

    let t = progress;
    for (let i = 0; i < 10; i++) {
        const yt = ((ay * t + by) * t + cy) * t - progress;
        const dyt = (3 * ay * t + 2 * by) * t + cy;
        if (Math.abs(dyt) < 1e-9) break;
        t -= yt / dyt;
    }
    t = Math.max(0, Math.min(1, t));

    const ax = 3 * p1x - 3 * p2x + 1;
    const bx = 3 * p2x - 6 * p1x;
    const cx = 3 * p1x;

    return ((ax * t + bx) * t + cx) * t;
}

export function normAngle(a: number): number {
    return ((a % 360) + 360) % 360;
}

export function computeWheelSegments(prizes: Prize[]): Segment[] {
    const anglePerSegment = 360 / prizes.length;
    let currentAngle = 0;
    const colors = generateWheelColors(prizes.length);

    return prizes.map((prize, index: number) => {
        const rawStart = currentAngle + 90;
        const rawEnd = currentAngle + anglePerSegment + 90;
        const startAngle = rawStart % 360;
        const endAngle = rawEnd % 360;

        currentAngle += anglePerSegment;

        const actual = endAngle > startAngle ? endAngle : endAngle + 360;

        // Scale the font down for longer labels so the whole name fits inside
        // the segment (the SVG viewBox couples wheel size and text size, so the
        // rendered text scales with the wheel too). ~70 user units of radial
        // room, ~0.6 units of width per character at font-size 1.
        const titleLines = segmentTitleWrap(prize.item.name);
        const longestLine = titleLines.reduce((max, line) => Math.max(max, line.length), 1);
        const fontSize = Math.min(9, Math.max(7, 70 / (longestLine * 0.6)));

        return {
            id: prize.item.id,
            fullTitle: prize.item.name,
            title: prize.item.name,
            titleLines,
            startAngle,
            endAngle: actual,
            backgroundColor: colors[index].background,
            textColor: colors[index].text,
            fontSize,
            path: segmentPath(100, 100, 100, startAngle, actual),
            transform: segmentTextTransform({ startAngle, endAngle: actual }),
            outerArcPath: segmentArcPath(100, 100, 100, startAngle, actual),
            outerBorderColor: darkenHex(colors[index].background, 25),
            selected: false,
            image: prize.item.image_url,
        };
    });
}

export function darkenHsl(hsl: string, amount: number): string {
    const match = /hsl\((\d+),\s*(\d+)%?,\s*(\d+)%?\)/i.exec(hsl);
    if (!match) return hsl;

    const [_, h, s, l] = match;
    const newL = Math.max(0, parseInt(l) - amount);
    return `hsl(${h}, ${s}%, ${newL}%)`;
}
